import { useState } from "react";
import { useDeviceDetail, useSearchMeters } from "../../hooks";
import { useBaseComponent } from "@shared/base/hooks/useBaseComponent";
import {
  BookDown,
  Edit2,
  EyeIcon,
  FileBarChart2Icon,
  History,
} from "lucide-react";
import { GenericTable } from "@shared/components/GenericTable";
import { Input } from "@shared/ui/input";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { MeterDeviceSearch } from "../../types";
import { TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { useSearchTracking } from "@shared/hooks/useSearchTracking";

interface MeterOption {
  id: string;
  meter_number: string;
  [key: string]: any;
}

interface MeterDevice {
  id: string;
  deviceNo: string;
  meterTypeDisplay: string;
  meterNumber: string;
  installedOn: string;
  premiseName: string;
  status: string;
  [key: string]: any;
}

export const MeterSearch: React.FC = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [selectedMeter, setSelectedMeter] = useState(null);
  const remoteUtilityId = useRemoteUtilityId();

  // Radio button state
  const [searchType, setSearchType] = useState<
    "device_number" | "meter_number"
  >("device_number");

  useSearchTracking({
  searchTerm: query,
  eventName: "Meter Search Performed",
  minLength: 2,
  additionalProps: { searchType },
});

  const radioDisplayName = searchType === "meter_number" ? "Meter" : "Device";
  const placeholder = `Enter ${radioDisplayName} Number (min 3 characters)`;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);

    // Clear selected meter and results when search is cleared
    if (value.trim() === "") {
      setSelectedMeter(null);
      return;
    }
  };

  const handleSelect = (meter: MeterDeviceSearch) => {
    setSelectedMeter(meter.id);
    setQuery(meter.meterNumber ?? 'NA' + " - " + meter.deviceNo);
  };

  // Only search when query length is 3 or more
  const shouldSearch = query.trim().length >= 3;

  // Hook for searching the meter or device for list
  const { data } = useSearchMeters({
    searchField: searchType,
    searchData: shouldSearch ? query : "", // Only pass query if it meets minimum length
    remoteUtilityId,
  });

  // Hook for fetching the list from the selected device/meter
  const { data: list } = useDeviceDetail({
    meterId: selectedMeter,
    remoteUtilityId,
  });
  
  const meterList = list ? (Array.isArray(list) ? list : [list]) : [];

  const handleAction = (key: string, data: any) => {
    switch (key) {
      case "view":
        navigate(`/mx/device-management/meter/view/${data.id}`);
        break;
      case "edit":
        navigate(`/mx/device-management/meter/edit/${data.id}`);
        break;
      default:
        break;
    }
  };

  // Define columns for GenericTable
  const columns: TableColumn<MeterDevice>[] = [
    {
      key: "deviceNo",
      header: "Device ID",
      sortable: true,
      render: (value: string) => (
        <span className="font-mono">{value}</span>
      ),
    },
    {
      key: "meterTypeDisplay",
      header: "Device Type",
      sortable: true,
      render: (value: string) => (
        <span className="font-medium">{value}</span>
      ),
    },
    {
      key: "meterNumber",
      header: "Meter Number",
      sortable: true,
      render: (value: string) => (
        <span className="font-mono">{value}</span>
      ),
    },
    {
      key: "installedOn",
      header: "Installation Date",
      sortable: true,
      render: (value: string) => (
        <span>{value ? new Date(value).toLocaleDateString() : "N/A"}</span>
      ),
    },
    {
      key: "premiseName",
      header: "Location",
      sortable: true,
      render: (value: string) => (
        <span className="max-w-xs truncate block" title={value}>
          {value || "N/A"}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (value: string) => {
        const getStatusVariant = (status: string) => {
          switch (status?.toLowerCase()) {
            case "active":
              return "default";
            case "inactive":
              return "secondary";
            case "maintenance":
              return "outline";
            case "error":
            case "failed":
              return "destructive";
            default:
              return "outline";
          }
        };
        
        return (
          <Badge variant={getStatusVariant(value)}>
            {value || "Unknown"}
          </Badge>
        );
      },
    },
  ];

  // Define actions for GenericTable
  const actions = [
    {
      label: "View Details",
      onClick: (row: MeterDevice) => {
        handleAction("view", row);
      },
      icon: <EyeIcon className="h-4 w-4 mr-2" />,
    },
    {
      label: "Edit Configuration",
      onClick: (row: MeterDevice) => {
        handleAction("edit", row);
      },
      icon: <Edit2 className="h-4 w-4 mr-2" />,
    },
  ];

  // Show results section only when a meter is selected
  const showResults = selectedMeter && query.trim() !== "";
  
  // Check if search has no results (invalid search)
  const hasSearched = shouldSearch; // Only consider it searched if minimum length is met
  const hasNoSearchResults = hasSearched && (!data || data.length === 0);



  return (
    <div className="flex flex-col gap-4 border rounded-xl p-4 my-4">
      <p className="page-subheading">Meter Search</p>
      
      <div className="flex items-center gap-4 mb-4">
        {["device_number", "meter_number"].map((type) => (
          <label key={type} className="cursor-pointer select-none">
            <input
              type="radio"
              name="searchType"
              value={type}
              checked={searchType === type}
              onChange={() =>
                setSearchType(type as "device_number" | "meter_number")
              }
              className="mr-2"
            />
            {type === "device_number" ? "Device" : "Meter"}
          </label>
        ))}
      </div>

      <Input
        type="text"
        placeholder={placeholder}
        value={query}
        onChange={handleInputChange}
        className="w-full"
      />

      {/* Show message when query is too short */}
      {query.trim().length > 0 && query.trim().length < 3 && (
        <div className="w-full border rounded p-2 mt-2 text-sm text-gray-600 bg-gray-50">
          <p>Please enter at least 3 characters to search.</p>
        </div>
      )}

      {/* Search dropdown results */}
      {data && data.length > 0 && shouldSearch && !selectedMeter && (
        <ul className="w-full shadow border rounded p-2 mt-2">
          {data.map((meter) => (
            <li
              key={meter?.id}
              className="hover:bg-gray-200 p-2 text-sm rounded cursor-pointer"
              onClick={() => handleSelect(meter)}
            >
              {meter.meterNumber + " - " + meter.deviceNo}
            </li>
          ))}
        </ul>
      )}

      {/* No data message for invalid search */}
      {hasNoSearchResults && !selectedMeter && (
        <div className="w-full shadow border rounded p-4 mt-2 text-center text-gray-500">
          <p>No {radioDisplayName.toLowerCase()} found matching your search.</p>
        </div>
      )}

      {/* Results section */}
      {showResults && (
        <div>
          <div className="flex justify-between w-full my-4">
            <p className="text-xl font-semibold">Search Results</p>
            <p>{meterList?.length || 0} results found</p>
          </div>
          
          {meterList && meterList.length > 0 ? (
            <GenericTable
              data={meterList}
              columns={columns}
              rowKey="id"
              selectable={false}
              actions={actions}
              actionsType="dropdown"
              emptyMessage="No data found for the selected meter/device."
              loading={false}
            />
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>No data found for the selected meter/device.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};